import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import {
  PermissionConfig,
  Role,
  checkPermission,
  checkMenuVisible,
  enforcePermissionRules
} from './permission';
// P2-O3: 审计操作人上下文绑定
import { setAuditOperator } from './db';

// ====== 配置同步：商家侧的后端配置快照 ======
export interface MerchantConfig {
  expireDate: string;        // YYYY-MM-DD 或 ''
  staffQuota: string;        // 数字字符串
  permissions: PermissionConfig | null;
  enabled: boolean;
  company_name?: string;
  displayName?: string;
  wechat_id?: string;
  configVersion: number;
}

// 认证用户接口
export interface AuthUser {
  employeeId: string;      // 兼容旧字段：账户ID
  employee_no?: string;    // 账户ID（4位大写排除I/O）- 服务端下发原始字段
  merchant_id?: string;    // 所属商家 username（员工即所属商家，商家自身即自己 username）
  name: string;
  role: Role;
  groupId: string | null;
  group_id?: string | null; // 兼容服务端原始字段
  permissions: PermissionConfig | null;
  // 商家端配置快照：登录时注入；safeFetch 检测到响应头版本号变化时刷新
  merchantConfig?: MerchantConfig;
}

// 全局刷新 hook：给 db.ts 这种非 React 上下文模块触发"拉取最新商家配置"
type RefreshFn = () => Promise<AuthUser | null>;
let _refreshMerchantConfigFn: RefreshFn | null = null;
export function registerMerchantConfigRefresh(fn: RefreshFn) { _refreshMerchantConfigFn = fn; }
export function refreshMerchantConfigGlobally(): Promise<AuthUser | null> {
  return _refreshMerchantConfigFn ? _refreshMerchantConfigFn() : Promise.resolve(null);
}

// 数据范围过滤条件
export interface DataScopeFilter {
  createdBy: string[]; // 可见数据的创建人姓名列表，空数组表示全部可见
}

// 开发模式标记：保留用于"宽松 JWT 回退、本地调试免真后端"等宽松验证能力
export const DEV_MODE = true;

// 桌面端是否强制经过登录屏：true 表示启动后必须先看到 /login，未登录无法进入工作台
export const FORCE_LOGIN_SCREEN = true;

// H5 模式标记（企微嵌入免登场景）
export const H5_MODE = {
  get isActive() {
    try {
      return !!(window as any).__H5_TOKEN__ ||
        new URLSearchParams(window.location.search).has('token');
    } catch (_) {
      return false;
    }
  }
};

// 开发模式默认用户（模拟管理员身份）
const DEV_USER: AuthUser = {
  employeeId: 'dev-admin',
  name: '管理员',
  role: 'admin',
  groupId: null,
  permissions: null
};

// H5 免登信息接口
export interface H5AuthInfo {
  token: string;
  user?: AuthUser;
}

// Auth 上下文接口
interface AuthContextType {
  user: AuthUser;
  // 桌面端是否已经完成真实登录（FORCE_LOGIN_SCREEN=true 时必须为 true 才能进入工作台）
  signedIn: boolean;
  // 是否处于 H5 模式（企微嵌入免登）
  h5Mode: boolean;
  // 检查模块某操作权限
  can: (module: string, action: string) => boolean;
  // 检查菜单是否可见
  canSeeMenu: (module: string) => boolean;
  // 获取数据范围过滤条件
  getDataScopeFilter: () => DataScopeFilter | null;
  // 是否是管理员
  isAdmin: boolean;
  // 是否是组长
  isLeader: boolean;
  // 是否是普通员工
  isStaff: boolean;
  // 设置当前用户（登录时调用）
  setUser: (user: AuthUser) => void;
  // 设置 H5 免登信息（企微嵌入时调用，免登）
  setH5Auth: (info: H5AuthInfo) => void;
  // 退出登录
  logout: () => void;
  // 增量/全量更新当前用户 merchantConfig（登录/版本变化时调用）
  updateMerchantConfig: (cfg: Partial<MerchantConfig> & { configVersion: number }) => void;
  // 主动从后端拉取最新商家配置并刷新 user
  refreshMerchantConfig: () => Promise<AuthUser | null>;
}

const AuthContext = createContext<AuthContextType | null>(null);

// AuthProvider 组件
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUserState] = useState<AuthUser>(DEV_USER);
  const [signedIn, setSignedIn] = useState<boolean>(!FORCE_LOGIN_SCREEN);
  const [h5Mode, setH5ModeState] = useState<boolean>(false);

  // 初始化：从持久化存储恢复真实登录态（桌面端必须经过登录屏）
  useEffect(() => {
    let activeUser = DEV_USER;
    let restored = false;

    // H5 模式/DEV 免登时，不强制要求登录屏
    if (!FORCE_LOGIN_SCREEN || H5_MODE.isActive) {
      setSignedIn(true);
    }

    // 无论 DEV_MODE 开关，只要启用了登录屏，就从 sessionStorage + localStorage 恢复登录态
    const hasAdminFlag = (() => {
      try {
        return localStorage.getItem('admin_logged_in') === '1';
      } catch (_) {
        return false;
      }
    })();

    const stored = sessionStorage.getItem('auth_user');
    if (stored) {
      try {
        const parsed = JSON.parse(stored) as AuthUser;
        // 管理端下发的 permissions（merchantConfig 里）优先于历史 user.permissions
        if (parsed.merchantConfig?.permissions) {
          parsed.merchantConfig.permissions = enforcePermissionRules(parsed.role, parsed.merchantConfig.permissions);
          parsed.permissions = parsed.merchantConfig.permissions;
        } else if (parsed.permissions) {
          parsed.permissions = enforcePermissionRules(parsed.role, parsed.permissions);
        }
        activeUser = parsed;
        setUserState(parsed);
        restored = true;
      } catch (e) {
        console.error('[AuthProvider] 解析用户信息失败:', e);
      }
    }

    // 有登录成功标记 + 恢复到了用户，就认为已登录
    if (FORCE_LOGIN_SCREEN && hasAdminFlag && restored) {
      setSignedIn(true);
    }

    // P2-O3: 应用启动时绑定初始审计操作人
    setAuditOperator(activeUser.employeeId, activeUser.name);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setUser = (newUser: AuthUser) => {
    // 强制应用权限规则（用户级权限 + 商家配置里的权限，后者覆盖前者）
    if (newUser.merchantConfig?.permissions) {
      newUser.merchantConfig.permissions = enforcePermissionRules(newUser.role, newUser.merchantConfig.permissions);
      newUser.permissions = newUser.merchantConfig.permissions;
    } else if (newUser.permissions) {
      newUser.permissions = enforcePermissionRules(newUser.role, newUser.permissions);
    }
    setUserState(newUser);
    setSignedIn(true);
    sessionStorage.setItem('auth_user', JSON.stringify(newUser));
    // P2-O3: 登录/切换用户时更新审计操作人
    setAuditOperator(newUser.employeeId, newUser.name);
  };

  // 新增/覆盖 merchantConfig，若权限更新则同时覆盖 user.permissions（用于 safeFetch 版本号变更后）
  const updateMerchantConfig = (cfg: Partial<MerchantConfig> & { configVersion: number }) => {
    setUserState(prev => {
      const newCfg: MerchantConfig = {
        expireDate: prev.merchantConfig?.expireDate || '',
        staffQuota: prev.merchantConfig?.staffQuota || '0',
        permissions: prev.merchantConfig?.permissions || null,
        enabled: prev.merchantConfig?.enabled ?? true,
        company_name: prev.merchantConfig?.company_name || '',
        displayName: prev.merchantConfig?.displayName || '',
        configVersion: 0,
        ...prev.merchantConfig,
        ...cfg
      };
      if (newCfg.permissions) newCfg.permissions = enforcePermissionRules(prev.role, newCfg.permissions);
      const nextUser: AuthUser = {
        ...prev,
        merchantConfig: newCfg,
        permissions: newCfg.permissions ?? prev.permissions
      };
      try { sessionStorage.setItem('auth_user', JSON.stringify(nextUser)); } catch (_) { /* ignore */ }
      return nextUser;
    });
  };

  // 主动从后端取最新商家配置（绕过 db.ts 避免循环依赖）
  const refreshMerchantConfig = async (): Promise<AuthUser | null> => {
    try {
      const token = localStorage.getItem('remote_token');
      if (!token) return null;
      const baseRaw = (localStorage.getItem('remote_base_url') || localStorage.getItem('remote_api_url') || 'https://shop.dtuitui.com').replace(/\/$/, '');
      const base = baseRaw.replace(/\/api$/, '');
      const resp = await fetch(`${base}/api/merchant/config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!resp.ok) return null;
      const json = await resp.json();
      const cfg = json?.merchantConfig as MerchantConfig | null | undefined;
      if (!cfg) return null;
      // 检测配置中 enabled=false：立即踢下线（防止后端未拦截时前端残留会话）
      if (cfg.enabled === false) {
        setTimeout(() => logout(), 50);
        return null;
      }
      updateMerchantConfig(cfg);
      let result: AuthUser | null = null;
      setUserState(prev => { result = prev; return prev; });
      return result;
    } catch (e) {
      console.warn('[refreshMerchantConfig] 静默拉取失败:', e);
      return null;
    }
  };

  // 挂载：把 refreshMerchantConfig 注册到全局，供 db.ts 拦截响应头时触发
  useEffect(() => {
    registerMerchantConfigRefresh(refreshMerchantConfig);
    return () => registerMerchantConfigRefresh(() => Promise.resolve(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 监听 db.ts 派发的"配置版本号变更"全局事件 → 静默拉取最新配置
  useEffect(() => {
    const handler = () => { void refreshMerchantConfig(); };
    const evt = 'merchant-config-version-changed' as any;
    window.addEventListener(evt, handler);
    return () => window.removeEventListener(evt, handler);
  }, [refreshMerchantConfig]);

  // 监听 db.ts 派发的"401 需要重新登录"全局事件（改密码/账号禁用/过期）→ 立即 logout 跳登录屏
  useEffect(() => {
    const handler = (ev: any) => {
      const reason = ev?.detail?.reason || '';
      // 做一次 setTimeout 避免打断当前 fetch 调用链（patch 代码在 .then 回调里）
      setTimeout(() => {
        try {
          if (reason) console.warn('[Auth] 会话失效，强制下线:', reason);
        } catch (_) {}
        logout();
      }, 0);
    };
    const evt = 'auth-requires-logout' as any;
    window.addEventListener(evt, handler);
    return () => window.removeEventListener(evt, handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setH5Auth = (info: H5AuthInfo) => {
    setH5ModeState(true);
    if (info.user) {
      const u = info.user;
      if (u.permissions) {
        u.permissions = enforcePermissionRules(u.role, u.permissions);
      }
      setUserState(u);
      setSignedIn(true);
      sessionStorage.setItem('auth_user', JSON.stringify(u));
      setAuditOperator(u.employeeId, u.name);
    } else {
      // H5 模式下即使没有用户也放行（免登），但保留现有 user 以避免权限系统报错
      setSignedIn(true);
    }
    if (info.token) {
      localStorage.setItem('remote_token', info.token);
      localStorage.setItem('remote_token_expiry', String(Date.now() + 7 * 24 * 60 * 60 * 1000));
    }
  };

  const logout = () => {
    // 清空桌面端登录态标记 + 用户信息 + 远程 token + 记住账号
    try {
      localStorage.removeItem('admin_logged_in');
      localStorage.removeItem('remote_token');
      localStorage.removeItem('remote_token_expiry');
      localStorage.removeItem('remembered_username');
      localStorage.removeItem('remembered_password');
      localStorage.removeItem('remember_me');
    } catch (_) { /* ignore */ }
    try {
      sessionStorage.removeItem('auth_user');
    } catch (_) { /* ignore */ }

    // 回退到开发用户占位（权限系统不报错），signedIn=false 触发路由守卫跳登录屏
    setUserState(DEV_USER);
    setSignedIn(false);
    setH5ModeState(false);
    setAuditOperator('', '');

    // AuthProvider 不在 Router 内，直接改 hash 跳到登录（HashRouter 会响应）
    try {
      window.location.hash = '#/login';
    } catch (_) { /* ignore */ }
  };

  const can = (module: string, action: string): boolean => {
    return checkPermission(user.permissions, user.role, module, action);
  };

  const canSeeMenu = (module: string): boolean => {
    return checkMenuVisible(user.permissions, user.role, module);
  };

  const getDataScopeFilter = (): DataScopeFilter | null => {
    // admin/merchant 看全部数据
    if (user.role === 'admin' || user.role === 'merchant') return null;
    // leader 看本组成员数据
    // staff 看自己数据
    // 注：实际查询时需要结合员工列表/工作组成员列表计算
    // 这里返回基础标识，业务页面需调用 fetchTeamMembers 获取成员姓名列表
    return {
      createdBy: [user.name] // 默认只看自己，leader 会在业务页面扩展
    };
  };

  const value: AuthContextType = {
    user,
    signedIn,
    h5Mode,
    can,
    canSeeMenu,
    getDataScopeFilter,
    isAdmin: user.role === 'admin' || user.role === 'merchant',
    isLeader: user.role === 'leader',
    isStaff: user.role === 'staff',
    setUser,
    setH5Auth,
    logout,
    updateMerchantConfig,
    refreshMerchantConfig
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// useAuth Hook
export function useAuth(): AuthContextType {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth 必须在 AuthProvider 内部使用');
  }
  return ctx;
}

// usePermission Hook（便捷版）
export function usePermission(module: string, action: string): boolean {
  const { can } = useAuth();
  return can(module, action);
}

// FIXED: 阶段4 - 数据范围过滤辅助函数
// 根据当前用户角色过滤业务数据：admin/merchant看全部 / leader看本组 / staff看自己
// 老数据(createdBy为空)默认所有人可见
export async function filterDataByScope<T extends { createdBy?: string }>(
  data: T[],
  user: AuthUser
): Promise<T[]> {
  // admin/merchant 看全部数据
  if (user.role === 'admin' || user.role === 'merchant') return data;

  // staff 只看自己创建的数据 + 老数据
  if (user.role === 'staff') {
    return data.filter(item => !item.createdBy || item.createdBy === user.name);
  }

  // leader 看本组成员创建的数据 + 老数据
  if (user.role === 'leader' && user.groupId) {
    try {
      const { fetchAll, COL } = await import('./db');
      const members = await fetchAll<{ employeeId: string }>(COL.WORKGROUP_MEMBERS, { groupId: user.groupId });
      const employees = await fetchAll<{ _id?: string; name: string }>(COL.EMPLOYEES);
      const memberNames = members
        .map(m => employees.find(e => e._id === m.employeeId)?.name)
        .filter(Boolean) as string[];
      memberNames.push(user.name); // 包含自己
      return data.filter(item => !item.createdBy || memberNames.includes(item.createdBy));
    } catch (e) {
      console.error('[filterDataByScope] 查询本组成员失败:', e);
      return data.filter(item => !item.createdBy || item.createdBy === user.name);
    }
  }

  // 默认：只看自己 + 老数据
  return data.filter(item => !item.createdBy || item.createdBy === user.name);
}

// FIXED: 阶段4 - 获取当前用户姓名（用于addDoc时写入createdBy）
export function getCurrentCreatorName(user: AuthUser): string {
  return user.name || '未知';
}
